import { Guid } from '../../model/guid';
import { FinishedItems } from '../../reducers/student/tracking/itemsTrackingReducer';
import { PreviouslySelectedOptionsForItems } from '../../reducers/student/tracking/questionWidgetTrackingReducer';
import { TrackedQuizState } from '../../reducers/student/tracking/trackedQuizReducer';
import { CourseCompletionStatus, QuizStatus, TrackingData, TrackingDataVersion } from './clients/trackingClient';
import { ITrackingCompletionService } from './trackingCompletionService';
import * as Immutable from 'immutable';

interface TrackingStateChangedResult {
  newTrackingData: TrackingData;
  finishedItemsChanged: boolean;
}

interface HandleParams {
  previousFinishedItems: FinishedItems;
  previousQuestionWidgetsState: PreviouslySelectedOptionsForItems;
  newFinishedItems: FinishedItems;
  newQuestionWidgetsState: PreviouslySelectedOptionsForItems;
  trackedItems: Immutable.Set<string>;
  previousTrackedQuizState: TrackedQuizState;
  newTrackedQuizState: TrackedQuizState;
}

export class TrackingPersistenceMiddlewareHandler {
  constructor(
    private readonly createTrackingCompletionService: () => ITrackingCompletionService,
    private readonly updatePersistedData: (trackingData: TrackingData) => Promise<void> | undefined,
    private readonly setCompletionStatus: (courseCompletionStatus: CourseCompletionStatus) => Promise<void> | undefined,
    private readonly setQuizStatus: (quizStatus: QuizStatus) => Promise<void> | undefined
  ) {}

  handle = ({
    previousFinishedItems,
    previousQuestionWidgetsState,
    newFinishedItems,
    newQuestionWidgetsState,
    trackedItems,
    previousTrackedQuizState,
    newTrackedQuizState,
  }: HandleParams): void => {
    const compareResult = this.compareTrackingStates(
      previousFinishedItems,
      previousQuestionWidgetsState,
      newFinishedItems,
      newQuestionWidgetsState
    );

    // check if quiz status changed
    if (previousTrackedQuizState !== newTrackedQuizState && newTrackedQuizState.passed !== null) {
      console.log('Quiz status changed');
      this.handleQuizStatusChanged(newTrackedQuizState);
    }

    if (!compareResult) {
      return;
    }

    void this.updatePersistedData(compareResult.newTrackingData);

    if (compareResult.finishedItemsChanged) {
      this.handleFinishedItemsChanged(trackedItems);
    }
  };

  private compareTrackingStates = (
    previousFinishedItems: FinishedItems,
    previousQuestionWidgetsState: PreviouslySelectedOptionsForItems,
    newFinishedItems: FinishedItems,
    newQuestionWidgetsState: PreviouslySelectedOptionsForItems
  ): TrackingStateChangedResult | null => {
    const finishedItemsChanged = !previousFinishedItems.equals(newFinishedItems);
    const trackingChanged = finishedItemsChanged || !previousQuestionWidgetsState.equals(newQuestionWidgetsState);

    if (trackingChanged) {
      const result: TrackingStateChangedResult = {
        newTrackingData: {
          finishedItems: newFinishedItems,
          widgetTrackingData: newQuestionWidgetsState,
          version: TrackingDataVersion.v2,
        },
        finishedItemsChanged,
      };
      return result;
    }

    return null;
  };

  private handleFinishedItemsChanged = (trackedItemsIds: Immutable.Set<Guid>): void => {
    if (trackedItemsIds.size === 0) {
      return;
    }

    const isEveryItemComplete = this.createTrackingCompletionService().areAllItemsCompleted(trackedItemsIds);
    if (isEveryItemComplete) {
      void this.setCompletionStatus(CourseCompletionStatus.Completed);
    }
  };
  private handleQuizStatusChanged = (trackedQuiz: TrackedQuizState): void => {
    if (trackedQuiz.passed === null || trackedQuiz.score === null) {
      throw new Error('Passed and score cannot be null.');
    }
    const quizStatus: QuizStatus = { passed: trackedQuiz.passed, score: trackedQuiz.score };
    if (quizStatus) {
      void this.setQuizStatus(quizStatus);
    }
  };
}
